<template>
  <div class="BaseCounter">
    <button
      type="button"
      class="BaseCounter__button BaseCounter__button--decrement"
      :disabled="props.disabled"
      @mouseup="handleDecrementClick"
      @touchend.prevent="handleDecrementClick"
    >
      <img src="@/assets/images/icon-minus-gray.svg" alt="감소" />
    </button>
    <input
      :value="modelValue"
      type="number"
      :disabled="props.disabled"
      readonly
      autocomplete="off"
      class="BaseCounter__input"
    />
    <button
      type="button"
      class="BaseCounter__button BaseCounter__button--increment"
      :disabled="props.disabled"
      @mouseup="handleIncrementClick"
      @touchend.prevent="handleIncrementClick"
    >
      <img src="@/assets/images/icon-plus-gray.svg" alt="증가" />
    </button>
  </div>
</template>

<script setup lang="ts">
  import { useCommonStore } from '@/stores/common';
  import { useI18n } from 'vue-i18n';

  interface Props {
    modelValue: number;
    min?: number;
    max?: number;
    disabled?: boolean;
    limit?: number;
  }

  const { t } = useI18n();
  const props = withDefaults(defineProps<Props>(), {
    modelValue: 1,
    min: 1,
    max: 99,
    disabled: false,
    limit: undefined,
  });

  const commonStore = useCommonStore();
  const emits = defineEmits(['update:modelValue']);

  const handleIncrementClick = () => {
    let count = props.modelValue + 1;

    if (count > props.max) {
      count = props.max;
    }

    if (props.limit && count > props.limit) {
      count = props.limit;
      commonStore.showDialog({
        type: 'alert',
        message: t('error.limit_item', props.limit),
      });
    }
    emits('update:modelValue', count);
  };

  const handleDecrementClick = () => {
    // 최소주문수량 체크
    if (props.min && props.min > 1 && props.modelValue <= props.min) {
      commonStore.showDialog({
        type: 'alert',
        message: t('error.min_item', { count: props.min }),
      });
      emits('update:modelValue', props.modelValue);
    } else {
      emits('update:modelValue', props.modelValue <= 1 ? 1 : props.modelValue - 1);
    }
  };
</script>

<style scoped lang="scss">
  @use '@/assets/scss/variables' as vars;

  .BaseCounter {
    display: flex;
    align-items: center;
    height: 3.8806vw;
    @media (max-width: vars.$md) {
      height: 30px;
    }
    .BaseCounter__button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 3.8806vw;
      height: 100%;
      border: 0.0746vw solid var(--border-color-primary);
      background: #fff;
      @media (max-width: vars.$md) {
        width: 30px;
        border-width: 1px;
      }
      img {
        width: 2vw;
        @media (max-width: vars.$md) {
          width: 12px;
        }
      }
    }
    .BaseCounter__input {
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      width: 5.9701vw;
      height: 100%;
      font-size: 2.0149vw;
      font-weight: 700;
      border-top: 0.0746vw solid var(--border-color-primary);
      border-bottom: 0.0746vw solid var(--border-color-primary);
      border-left: none;
      border-right: none;
      outline: 0;
      border-radius: 0;
      @media (max-width: vars.$md) {
        width: 40px;
        font-size: 14px;
        border-top-width: 1px;
        border-bottom-width: 1px;
      }
    }

    &.small {
      height: 3.1343vw;
      @media (max-width: vars.$lg) {
        height: 30px;
      }
      @media (max-width: vars.$md) {
        height: 24px;
      }
      .BaseCounter__button {
        width: 3.1343vw;
        @media (max-width: vars.$lg) {
          width: 30px;
        }
        @media (max-width: vars.$md) {
          width: 24px;
        }
        img {
          width: 1.4925vw;
          @media (max-width: vars.$lg) {
            width: 14px;
          }
          @media (max-width: vars.$md) {
            width: 10px;
          }
        }
      }
      .BaseCounter__input {
        width: 4.6269vw;
        font-size: 1.4925vw;
        @media (max-width: vars.$lg) {
          width: 30px;
          font-size: 16px;
        }
        @media (max-width: vars.$md) {
          width: 24px;
          font-size: 12px;
        }
      }
    }
  }
</style>
